import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är green screen?",
    a: "Green screen (chroma key) är en inspelningsteknik där personen filmas mot en jämnt belyst grön bakgrund. I efterbehandlingen görs den gröna färgen transparent så att bakgrunden kan bytas mot grafik, en annan miljö eller en ren varumärkesfärg.",
  },
  {
    q: "Varför används just grön färg?",
    a: "Grönt ligger längst från mänskliga hudtoner i färgrymden och digitala kamerasensorer registrerar mest information i den gröna kanalen. Blå duk används när motivet innehåller grönt — till exempel gröna kläder, växter eller en grön logotyp.",
  },
  {
    q: "Vad kostar en green screen-inspelning?",
    a: "En halvdag i studio med green screen ligger typiskt på 25 000–45 000 kr inklusive team, ljussättning och grundklipp. En heldag med flera talare och grafikpaket landar på 45 000–90 000 kr. Löpande behov täcks av abonnemang från 14 995 kr/mån.",
  },
  {
    q: "Vad krävs för ett bra resultat?",
    a: "Jämn belysning av duken utan skuggor, minst 1,5–2 meter mellan person och duk för att undvika grön spill på hår och axlar, separat ljussättning av personen, samt inspelning i så hög färgupplösning som möjligt (helst 4:2:2 eller bättre).",
  },
  {
    q: "Kan man använda green screen på plats hos oss?",
    a: "Ja. Vi tar med portabel duk och ljus och bygger upp en tillfällig studio i era lokaler. Det kräver cirka 4 x 4 meter fri yta och takhöjd på minst 2,5 meter för att ljussättningen ska fungera.",
  },
  {
    q: "När bör man välja något annat än green screen?",
    a: "När miljön i sig bygger trovärdighet — verkstad, butik, kontor eller kund på plats — ger en riktig location mer. Green screen vinner när samma inspelning ska återanvändas i många varianter, språk eller kampanjbakgrunder.",
  },
];

export const Route = createFileRoute("/green-screen")({
  head: () => ({
    meta: [
      { title: "Green screen — en inspelning, hur många versioner som helst" },
      {
        name: "description",
        content:
          "Green screen förklarat: teknik, ljussättning, vanliga misstag och pris för inspelning i Sverige 2026. Guide från ett produktionsbolag som gör det varje vecka.",
      },
      { property: "og:title", content: "Green screen — så fungerar det och vad det kostar" },
      {
        property: "og:description",
        content: "Chroma key, ljussättning, spill och pris — allt ni behöver veta innan ni bokar en green screen-inspelning.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/green-screen" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/green-screen" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/green-screen", name: "Green screen — en inspelning, hur många versioner som helst", description: "Green screen förklarat: teknik, ljussättning, vanliga misstag och pris för inspelning i Sverige 2026. Guide från ett produktionsbolag som gör det varje vecka."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Green screen-inspelning",
            path: "/green-screen",
            serviceType: "Studioinspelning",
            description:
              "Green screen-inspelning (chroma key) i studio eller på plats, med keying, grafik och versionering.",
            priceRange: "25 000–90 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Green screen", path: "/green-screen" },
          ]),
        ),
      },
    ],
  }),
  component: GreenScreenPage,
});

function GreenScreenPage() {
  return (
    <LandingPage
      eyebrow="Green screen"
      title={<>Green screen — en inspelning, obegränsat med bakgrunder.</>}
      lede="Green screen låter er spela in en gång och sedan byta miljö, språkversion och grafik hur många gånger ni vill. Vi gör det i studio eller bygger upp en tillfällig studio hos er — med rätt ljussättning så att keyingen faktiskt håller."
      tldr={
        <p>
          Green screen (chroma key) innebär att motivet filmas mot en jämnt
          belyst grön duk som sedan görs transparent i efterbehandlingen. Pris
          i Sverige: <strong>25 000–45 000 kr</strong> för en halvdag,{" "}
          <strong>45 000–90 000 kr</strong> för en heldag med grafikpaket. Rätt
          ljussättning och 1,5–2 meters avstånd till duken avgör kvaliteten.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="green-screen"
      bullets={[
        { title: "Studio eller på plats", body: "Fast studio, eller portabel duk och ljus i era lokaler." },
        { title: "Keying som håller", body: "Rätt avstånd, spill-suppression och separat ljussättning av personen." },
        { title: "Många versioner", body: "En inspelning blir flera bakgrunder, språk och format." },
      ]}
      pricing={{
        heading: "Vad kostar green screen?",
        intro: "Riktpriser exklusive moms för inspelning i Sverige.",
        rows: [
          { format: "Halvdag studio", scope: "1 talare, ljus, ljud, keying, grundklipp", price: "25 000–45 000 kr", lead: "2 veckor" },
          { format: "Heldag studio", scope: "Flera talare, grafikpaket, versionering", price: "45 000–90 000 kr", lead: "2–3 veckor" },
          { format: "Portabel studio hos er", scope: "Duk, ljus och team på plats", price: "+8 000–15 000 kr", lead: "enligt ovan" },
          { format: "Löpande inspelning", scope: "Abonnemang, block­inspelning varje månad", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
        footnote: "Grafik, animerade bakgrunder och 3D-miljöer prissätts separat efter omfattning.",
      }}
      process={{
        heading: "Så går en green screen-inspelning till",
        steps: [
          { label: "Manus och bakgrundsplan", body: "Vad som ska sägas och vad som ska synas bakom — bestäms innan inspelning.", duration: "vecka 1" },
          { label: "Rigg och ljussättning", body: "Duken belyses jämnt, motivet ljussätts separat med kontrollerad kantljus.", duration: "inspelningsdag" },
          { label: "Inspelning", body: "Teleprompter vid behov, flera tagningar och alternativa längder.", duration: "0,5–1 dag" },
          { label: "Keying och komposition", body: "Chroma key, spill-suppression, bakgrund, grafik och färgmatchning.", duration: "vecka 2" },
          { label: "Versionering", body: "16:9, 9:16 och 1:1 med undertexter och eventuella språkversioner.", duration: "vecka 2–3" },
        ],
      }}
      sections={[
        {
          id: "vanliga-misstag",
          heading: "Fyra misstag som förstör keyingen",
          body: (
            <>
              <p>
                <strong>1. Ojämnt ljus på duken.</strong> Skuggor och hot spots gör
                att keyern måste kompromissa och kanterna blir taggiga.{" "}
                <strong>2. För kort avstånd.</strong> Står personen nära duken
                studsar grönt ljus upp i hår och axlar.
              </p>
              <p>
                <strong>3. Fel garderob.</strong> Gröna eller starkt reflekterande
                plagg försvinner i keyingen — och genomskinliga tyger blir
                omöjliga att maskera. <strong>4. För hård komprimering.</strong>{" "}
                Material inspelat i 4:2:0 med låg bitrate ger fransiga kanter som
                ingen efterbehandling räddar helt.
              </p>
            </>
          ),
        },
        {
          id: "nar-passar-det",
          heading: "När green screen lönar sig",
          body: (
            <>
              <p>
                Vid utbildnings- och instruktionsfilm där samma talare ska
                kombineras med skiftande grafik, vid produktlanseringar med många
                marknadsversioner, och vid{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">företagsfilm</a>{" "}
                där ni vill ha en konsekvent visuell ram över tid.
              </p>
              <p>
                Ska bakgrunden vara animerad grafik snarare än en miljö är{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">motion graphics</a>{" "}
                ofta ett naturligt komplement i samma produktion.
              </p>
            </>
          ),
        },
        {
          id: "alternativ",
          heading: "Alternativ till green screen",
          body: (
            <p>
              Riktig location när miljön bygger trovärdighet, ren studiobakgrund i
              varumärkesfärg när ni vill ha enkelhet, eller helt{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>{" "}
              när ingen behöver synas i bild.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      closing={{
        title: "Planerar ni en green screen-inspelning?",
        body: "Boka 30 minuter så går vi igenom upplägg, studiobehov och pris för ert case.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
